import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { ITicketSyncEvent, SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { EventTicket } from './event-ticket.js';

export class TicketScannedEvent extends SyncEvent implements ITicketSyncEvent {
  static readonly TYPE_ID: string = '2ed09e7a014a4cf5888de629eba3e281';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly ticketId: string,
      public readonly ticket: EventTicket,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketScannedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketScannedEvent {
    return new TicketScannedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<string>(map, 'ticketId'),
        context.fromData<EventTicket>(MapUtils.get<SerializedData>(map, 'ticket')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'ticketId': this.ticketId,
      'ticket': context.toData(this.ticket),
    };
  }

  clone(): this {
    return new TicketScannedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.ticketId,
        this.ticket.clone(),
    ) as this;
  }

  isSame(other: TicketScannedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.ticketId === this.ticketId &&
        isSameData(other.ticket, this.ticket);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof TicketScannedEvent;
  }

  static assertType(order: DataModel): order is TicketScannedEvent {
    return order instanceof TicketScannedEvent;
  }
}

export class TicketScannedEventBuilder extends DataModelBuilder<TicketScannedEvent> {
  build(context: SerializationContext, map: SerializedData): TicketScannedEvent {
    return TicketScannedEvent.fromMap(context, map);
  }
}
